<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
// Loaded here rather than relied on: the locale is global once imported, so a
// component that only ever ran after another had imported it would print
// French dates by accident and English ones the day it loads first.
import "dayjs/locale/fr";
import {ArrowRight, CircleAlert, Hand, History, TriangleAlert} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {
    Dialog,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogScrollContent,
    DialogTitle
} from "@/core/components/ui/dialog";
import useAssistantDecision from "@/ai-assistant/composables/useAssistantDecision";
import useTeamNames from "@/ai-assistant/composables/useTeamNames";
import {propositionDiff, DiffRange} from "@/ai-assistant/lib/propositionDiff";
import {measure, sourceLabel} from "@/ai-assistant/lib/propositionFormat";
import {Proposition} from "@/ai-assistant/interfaces";
import {DIFF_KIND_LABELS, FOLDED_SEVERITIES, MANUAL_OPERATION_LABELS} from "@/ai-assistant/constants";

const {proposition} = defineProps<{
    proposition: Proposition;
}>();

const open = defineModel<boolean>('open', {default: false});

const emits = defineEmits<{
    (event: 'adjust'): void;
}>();

const {nameOf} = useTeamNames();
const {pending, failure, expired, apply, reject} = useAssistantDecision();

/** Read from the operations, never recomputed: the totals are the impacts. */
const days = computed(() => propositionDiff(proposition.operations ?? []));

const decided = computed<boolean>(() =>
    ['applied', 'rejected', 'cancelled', 'expired'].includes(proposition.state)
);

/**
 * A scenario the planning has outrun.
 *
 * Frequent rather than exceptional: two people work in parallel in an
 * officine, and one of them recording an absence is enough. So the modal says
 * what happened and offers to ask again, rather than presenting two dead
 * buttons and leaving the pharmacist to guess.
 */
const outdated = computed<boolean>(() => proposition.state === 'expired');

const outstanding = computed(() =>
    (proposition.conformity?.violations ?? [])
        .filter(violation => ! FOLDED_SEVERITIES.includes(violation.severity))
);

const dayLabel = (date: string): string =>
    date ? dayjs(date).locale('fr').format('dddd D MMMM') : '';

const rangeLabel = (ranges: DiffRange[]): string =>
    ranges.length ? ranges.map(range => `${range.starts_at}–${range.ends_at}`).join(' · ') : 'rien';

/**
 * Applying closes the modal; rejecting leaves it open just long enough for the
 * state to change under the pharmacist's eyes, then closes too. A refusal
 * keeps it open — the explanation is the point.
 */
const decide = async (action: (id: number) => Promise<boolean>) => {
    if (await action(proposition.id)) {
        open.value = false;
    }
};

const askForAdjustment = () => {
    open.value = false;
    emits('adjust');
};
</script>

<template>
  <Dialog v-model:open="open">
    <!--
      The single column may shrink below its content: otherwise the row of
      actions, which cannot wrap, sets the width and pushes the text out.
    -->
    <DialogScrollContent class="max-w-xl grid-cols-[minmax(0,1fr)]">
      <DialogHeader>
        <DialogTitle class="text-sm text-primary">
          Ce que ce scénario changerait
        </DialogTitle>
        <DialogDescription class="text-[11px] text-gray-dark/70">
          Rien n'est enregistré tant que vous n'avez pas cliqué. Vous pouvez revenir en arrière
          après application.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-3">
        <!--
          Repeated here because this is where the decision is taken: the alert
          is one click from being overridden, and the application is not
          refused — the pharmacist is the employer.
        -->
        <p
          v-for="violation in outstanding"
          :key="violation.rule_id"
          class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
        >
          <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
          <span class="min-w-0 flex-1">
            {{ violation.label }}
            <span v-if="sourceLabel(violation.source)" class="text-gray-dark/60">
              — {{ sourceLabel(violation.source) }}
            </span>
          </span>
        </p>

        <section v-if="days.length">
          <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
            Horaires
          </p>
          <ul class="space-y-1.5">
            <li
              v-for="(day, index) in days"
              :key="`${day.memberId}-${day.date}-${index}`"
              class="rounded-lg border border-primary-lightest px-2.5 py-1.5"
            >
              <p class="text-[11px] font-semibold text-gray-dark">
                {{ nameOf(day.memberId) }} · {{ dayLabel(day.date) }}
              </p>
              <!--
                The previous hours are shown only when the operation recorded
                them. A day the decision layer clears carries no `avant`, and
                printing « rien » there would say the day was empty when it is
                precisely the day being emptied.
              -->
              <p class="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-dark/70">
                <template v-if="day.before !== null">
                  <span class="tabular-nums line-through decoration-gray-dark/30">{{ rangeLabel(day.before) }}</span>
                  <ArrowRight class="size-3 shrink-0 text-gray-dark/40" />
                </template>
                <span class="font-semibold tabular-nums text-gray-dark">{{ rangeLabel(day.after) }}</span>
                <span class="rounded-full bg-gray-light px-1.5 text-[10px] text-gray-dark/60">
                  {{ DIFF_KIND_LABELS[day.kind] }}
                </span>
              </p>
            </li>
          </ul>
        </section>

        <section v-if="proposition.impacts?.length">
          <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
            Ce que cela change
          </p>
          <ul class="space-y-1">
            <li
              v-for="(impact, index) in proposition.impacts"
              :key="`impact-${index}`"
              class="flex items-baseline justify-between gap-2 text-[11px]"
            >
              <span class="min-w-0 flex-1 text-gray-dark/80">{{ impact.metric }}</span>
              <span class="shrink-0 tabular-nums text-gray-dark/60">{{ measure(impact.before, impact.unit) }}</span>
              <span class="shrink-0 text-gray-dark/40">→</span>
              <span class="shrink-0 font-semibold tabular-nums text-gray-dark">{{ measure(impact.after, impact.unit) }}</span>
            </li>
          </ul>
        </section>

        <!-- Never written by the application: said so before the click, not after. -->
        <section v-if="proposition.manual_operations?.length">
          <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
            Resteront à faire dans l'agenda
          </p>
          <p
            v-for="(operation, index) in proposition.manual_operations"
            :key="`manuelle-${index}`"
            class="flex items-start gap-1.5 text-[11px] leading-snug text-gray-dark/80"
          >
            <Hand class="mt-px size-3 shrink-0 text-primary/60" />
            {{ MANUAL_OPERATION_LABELS[operation.op] ?? 'Action à réaliser vous-même' }}
          </p>
        </section>

        <p
          v-if="outdated && !failure"
          class="flex items-start gap-1.5 rounded-lg bg-gray-light px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
        >
          <History class="mt-px size-3.5 shrink-0 text-gray-dark/60" />
          <span class="min-w-0 flex-1">
            Le planning a changé depuis le calcul de ce scénario : il ne peut plus être appliqué
            tel quel.
            <button
              class="mt-1 block text-left font-semibold text-primary underline"
              @click="askForAdjustment"
            >
              Reposer la question
            </button>
          </span>
        </p>

        <p
          v-if="failure"
          class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
        >
          <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
          <span class="min-w-0 flex-1">
            {{ failure }}
            <button
              v-if="expired"
              class="mt-1 block text-left font-semibold text-primary underline"
              @click="askForAdjustment"
            >
              Reposer la question
            </button>
          </span>
        </p>
      </div>

      <DialogFooter class="gap-1.5 sm:flex-wrap sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="h-8 text-xs text-gray-dark/70"
          @click="open = false"
        >
          Revenir au scénario
        </Button>

        <div class="flex flex-wrap justify-end gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="h-8 text-xs"
            :disabled="pending"
            @click="askForAdjustment"
          >
            Demander un ajustement
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="h-8 text-xs"
            :disabled="pending || decided"
            @click="decide(reject)"
          >
            Écarter
          </Button>
          <Button
            type="button"
            size="sm"
            class="h-8 bg-primary text-xs text-white hover:bg-primary/90"
            :disabled="pending || decided"
            @click="decide(apply)"
          >
            Appliquer
          </Button>
        </div>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>
